import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import toast from 'react-hot-toast';
import { handleUnauthorized } from '../../../TokenAuth/auth';
import PageHeader1 from '../../../components/common/PageHeader1';

function FreeChatRouting() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRuleId, setCurrentRuleId] = useState(null);

  const initialFormState = {
    ruleName: '',
    priority: 1,
    isActive: true,
    conditions: {
      walletBalance: { operator: '>=', value: 0 },
      rechargeCount: { operator: 'any', value: 0 },
      consultationCount: { operator: 'any', value: 0 },
      sessionOutcome: 'quit',
    },
    action: {
      redirectScreen: 'Connect',
      showPopup: true,
      popupMessage: '',
      showRecommendedAstrologers: true,
    },
  };

  const mockData = [];

  const [formData, setFormData] = useState(initialFormState);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('astroVani-admin-token');
      const response = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/freeChatRouting`, {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        setRules(response.data.data?.length ? response?.data?.data?.filter((item) => item?.ruleName?.includes('Scenario 3')) : mockData);
      } else {
        setRules(mockData);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error('Failed to fetch rules. Loading mock data.');
        setRules(mockData);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleOpenModal = (rule = null) => {
    if (rule) {
      setEditMode(true);
      setCurrentRuleId(rule._id);
      setFormData({
        ruleName: rule.ruleName || '',
        priority: rule.priority || 1,
        isActive: rule.isActive ?? true,
        conditions: {
          walletBalance: {
            operator: rule.conditions?.walletBalance?.operator || '>=',
            value: rule.conditions?.walletBalance?.value || 0,
          },
          rechargeCount: {
            operator: rule.conditions?.rechargeCount?.operator || 'any',
            value: rule.conditions?.rechargeCount?.value || 0,
          },
          consultationCount: {
            operator: rule.conditions?.consultationCount?.operator || 'any',
            value: rule.conditions?.consultationCount?.value || 0,
          },
          sessionOutcome: rule.conditions?.sessionOutcome || 'quit',
        },
        action: {
          redirectScreen: rule.action?.redirectScreen || 'Connect',
          showPopup: rule.action?.showPopup ?? true,
          popupMessage: rule.action?.popupMessage || '',
          showRecommendedAstrologers: rule.action?.showRecommendedAstrologers ?? true,
        },
      });
    } else {
      setEditMode(false);
      setCurrentRuleId(null);
      setFormData(initialFormState);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData(initialFormState);
    setEditMode(false);
    setCurrentRuleId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      const token = localStorage.getItem('astroVani-admin-token');
      const response = await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/freeChatRouting/${id}`, {
        headers: {
          'Authorization': `${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        toast.success('Rule deleted successfully');
        fetchRules();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error('Failed to delete rule');
      }
    }
  };

  const handleSave = async () => {
    if (!formData.ruleName) {
      toast.error('Rule Name is required');
      return;
    }
    try {
      const token = localStorage.getItem('astroVani-admin-token');
      const payload = {
        ruleName: formData.ruleName,
        priority: Number(formData.priority),
        isActive: formData.isActive,
        conditions: {
          walletBalance: {
            operator: formData.conditions.walletBalance.operator,
            value: Number(formData.conditions.walletBalance.value),
          },
          rechargeCount: {
            operator: formData.conditions.rechargeCount.operator,
            value: Number(formData.conditions.rechargeCount.value),
          },
          consultationCount: {
            operator: formData.conditions.consultationCount.operator,
            value: Number(formData.conditions.consultationCount.value),
          },
          sessionOutcome: formData.conditions.sessionOutcome,
        },
        action: {
          redirectScreen: formData.action.redirectScreen,
          showPopup: formData.action.showPopup,
          popupMessage: formData.action.popupMessage,
          showRecommendedAstrologers: formData.action.showRecommendedAstrologers,
        },
      };

      if (editMode && currentRuleId) {
        const response = await axios.patch(
          `${process.env.REACT_APP_BASEURL}/admin/freeChatRouting/${currentRuleId}`,
          payload,
          {
            headers: {
              Authorization: `${token}`,
            },
          }
        );
        if (response.data.success) {
          toast.success('Rule updated successfully');
        }
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BASEURL}/admin/freeChatRouting`,
          payload,
          {
            headers: {
              Authorization: `${token}`,
            },
          }
        );
        if (response.data.success) {
          toast.success('Rule created successfully');
        }
      }
      handleCloseModal();
      fetchRules();
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error(error.response?.data?.message || 'Failed to save rule');
      }
    }
  };

  const columns = [
    { field: 'ruleName', headerName: 'Rule Name', width: 250 },
    { field: 'priority', headerName: 'Priority', width: 100 },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <span className={`badge ${params.value ? 'bg-success' : 'bg-danger'}`}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      field: 'conditions',
      headerName: 'Session Outcome',
      width: 180,
      renderCell: (params) => params.value?.sessionOutcome || 'N/A'
    },
    {
      field: 'action',
      headerName: 'Redirect To',
      width: 180,
      renderCell: (params) => params.value?.redirectScreen || 'N/A'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Button size="small" variant="outlined" color="primary" sx={{ mr: 1 }} onClick={() => handleOpenModal(params.row)}>
            Edit
          </Button>
          {/* <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(params.row._id)}>
            Delete
          </Button> */}
        </Box>
      ),
    },
  ];

  return (
    <div className="card p-2 m-3">
      <PageHeader1
        pagetitle="Free Chat Routing Rules"
        routebutton={false}
      />
      {/* <PageHeader1
        pagetitle="Free Chat Routing Rules"
        modalbutton={() => (  
          <Button variant="contained" color="primary" onClick={() => handleOpenModal()}>
            Add New Rule
          </Button>
        )}
        routebutton={false}
      /> */}
      <div className="card-body">
        <div style={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rules}
            columns={columns}
            getRowId={(row) => row._id}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
          />
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Routing Rule' : 'Add Routing Rule'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* General Info */}
            <Typography variant="h6" className="mt-2">General</Typography>
            <Box display="flex" gap={2}>
              <TextField
                label="Rule Name"
                value={formData.ruleName}
                disabled={editMode}
                onChange={(e) => setFormData({ ...formData, ruleName: e.target.value })}
                fullWidth
                size="small"
              />
              {/*
              <TextField
                label="Priority"
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                fullWidth
                size="small"
              />*/}
            </Box>
            {/* <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Is Active"
            />

            <Divider className="my-2" />  */}

            {/* Conditions */}
            {/* <Typography variant="h6">Conditions</Typography>
            <Box display="flex" gap={2} mb={1}>
              <FormControl size="small" fullWidth>
                <InputLabel>Wallet Balance Operator</InputLabel>
                <Select
                  value={formData.conditions.walletBalance.operator}
                  label="Wallet Balance Operator"
                  onChange={(e) => setFormData({
                    ...formData,
                    conditions: { ...formData.conditions, walletBalance: { ...formData.conditions.walletBalance, operator: e.target.value } }
                  })}
                >
                  <MenuItem value="any">any</MenuItem>
                  <MenuItem value="==">==</MenuItem>
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value=">=">&gt;=</MenuItem>
                  <MenuItem value="<=">&lt;=</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Wallet Balance Value"
                type="number"
                value={formData.conditions.walletBalance.value}
                onChange={(e) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, walletBalance: { ...formData.conditions.walletBalance, value: e.target.value } }
                })}
                fullWidth
                size="small"
              />
            </Box>

            <Box display="flex" gap={2} mb={1}>
              <FormControl size="small" fullWidth>
                <InputLabel>Recharge Count Operator</InputLabel>
                <Select
                  value={formData.conditions.rechargeCount.operator}
                  label="Recharge Count Operator"
                  onChange={(e) => setFormData({
                    ...formData,
                    conditions: { ...formData.conditions, rechargeCount: { ...formData.conditions.rechargeCount, operator: e.target.value } }
                  })}
                >
                  <MenuItem value="any">any</MenuItem>
                  <MenuItem value="==">==</MenuItem>
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value=">=">&gt;=</MenuItem>
                  <MenuItem value="<=">&lt;=</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Recharge Count Value"
                type="number"
                value={formData.conditions.rechargeCount.value}
                onChange={(e) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, rechargeCount: { ...formData.conditions.rechargeCount, value: e.target.value } }
                })}
                fullWidth
                size="small"
              />
            </Box>

            <Box display="flex" gap={2} mb={1}>
              <FormControl size="small" fullWidth>
                <InputLabel>Consultation Count Operator</InputLabel>
                <Select
                  value={formData.conditions.consultationCount.operator}
                  label="Consultation Count Operator"
                  onChange={(e) => setFormData({
                    ...formData,
                    conditions: { ...formData.conditions, consultationCount: { ...formData.conditions.consultationCount, operator: e.target.value } }
                  })}
                >
                  <MenuItem value="any">any</MenuItem>
                  <MenuItem value="==">==</MenuItem>
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value=">=">&gt;=</MenuItem>
                  <MenuItem value="<=">&lt;=</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Consultation Count Value"
                type="number"
                value={formData.conditions.consultationCount.value}
                onChange={(e) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, consultationCount: { ...formData.conditions.consultationCount, value: e.target.value } }
                })}
                fullWidth
                size="small"
              />
            </Box>

            <FormControl size="small" fullWidth>
              <InputLabel>Session Outcome</InputLabel>
              <Select
                value={formData.conditions.sessionOutcome}
                label="Session Outcome"
                onChange={(e) => setFormData({
                  ...formData,
                  conditions: { ...formData.conditions, sessionOutcome: e.target.value }
                })}
              >
                <MenuItem value="any">any</MenuItem>
                <MenuItem value="completed">completed</MenuItem>
                <MenuItem value="quit">quit</MenuItem>
              </Select>
            </FormControl>

            <Divider className="my-2" /> */}

            {/* Actions */}
            <Typography variant="h6">Actions</Typography>
            {/* <FormControl size="small" fullWidth mb={2}>
              <InputLabel>Redirect Screen</InputLabel>
              <Select
                value={formData.action.redirectScreen}
                label="Redirect Screen"
                onChange={(e) => setFormData({
                  ...formData,
                  action: { ...formData.action, redirectScreen: e.target.value }
                })}
              >
                <MenuItem value="WalletPack">WalletPack</MenuItem>
                <MenuItem value="Connect">Connect</MenuItem>
                <MenuItem value="Home">Home</MenuItem>
              </Select>
            </FormControl> */}

            <Box display="flex" flexDirection="column" gap={1} mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.action.showPopup}
                    onChange={(e) => setFormData({ ...formData, action: { ...formData.action, showPopup: e.target.checked } })}
                  />
                }
                label="Show Popup"
              />
              {formData.action.showPopup && (
                <TextField
                  label="Popup Message"
                  value={formData.action.popupMessage}
                  onChange={(e) => setFormData({
                    ...formData,
                    action: { ...formData.action, popupMessage: e.target.value }
                  })}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
              )}
              {/* <FormControlLabel
                control={
                  <Switch
                    checked={formData.action.showRecommendedAstrologers}
                    onChange={(e) => setFormData({ ...formData, action: { ...formData.action, showRecommendedAstrologers: e.target.checked } })}
                  />
                }
                label="Show Recommended Astrologers"
              /> */}
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="inherit">Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FreeChatRouting;
